import { useState, useMemo } from 'react';
import { ChevronDown, Building2, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';

/** White-background multi-select dropdown for the Historical Data Centre —
 * lists every Samsara vehicle (not just ones already linked to a branch),
 * with a "select all" shortcut per saved branch group (e.g. C30/E70). */
export const VehiclePickerDropdown = ({ vehicles, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const branches = useMemo(() => [...new Set(vehicles.map((v) => v.branch_id).filter(Boolean))].sort(), [vehicles]);
  const filtered = vehicles.filter((v) => (v.vehicle_registration || v.name || '').toLowerCase().includes(query.toLowerCase()));

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };

  const selectBranch = (branchId) => {
    const next = new Set(selected);
    vehicles.filter((v) => v.branch_id === branchId).forEach((v) => next.add(v.samsara_vehicle_id));
    onChange(next);
  };

  const label = selected.size === 0 ? 'Select vehicles' : `${selected.size} vehicle${selected.size === 1 ? '' : 's'} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between gap-2 w-full text-sm bg-white border border-input rounded-md px-3 h-9 shadow-sm"
          data-testid="hdc-vehicle-picker-trigger"
        >
          <span className={selected.size === 0 ? 'text-muted-foreground' : 'text-gray-800 font-medium'}>{label}</span>
          <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-white p-3 space-y-2" align="start" data-testid="hdc-vehicle-picker-content">
        {branches.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {branches.map((b) => (
              <button
                key={b} type="button" onClick={() => selectBranch(b)}
                className="text-[11px] font-bold px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100"
                data-testid={`hdc-select-branch-${b}`}
              >
                Select all {b}
              </button>
            ))}
            <button type="button" onClick={() => onChange(new Set())} className="text-[11px] font-bold px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="hdc-clear-vehicles">
              Clear
            </button>
          </div>
        )}
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search vehicles…" className="pl-8 h-8 text-sm bg-white" data-testid="hdc-vehicle-search" />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {filtered.length === 0 ? (
            <div className="text-xs text-gray-400 py-2 text-center">No vehicles match</div>
          ) : filtered.map((v) => (
            <label key={v.samsara_vehicle_id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`hdc-vehicle-option-${v.samsara_vehicle_id}`}>
              <input type="checkbox" checked={selected.has(v.samsara_vehicle_id)} onChange={() => toggle(v.samsara_vehicle_id)} className="accent-orange-600" />
              <Building2 className="w-3 h-3 text-gray-400 shrink-0" />
              <span className="truncate flex-1">{v.vehicle_registration || v.name}</span>
              {v.branch_id && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${v.branch_id === 'E70' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>{v.branch_id}</span>
              )}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default VehiclePickerDropdown;
